// Copyright (c) StrangeBee 2023

import React, { ReactElement, ReactNode } from 'react';
import { Layout } from 'antd';
import { THHeader } from '@Organisms/THEntityLayout/THHeader/THHeader';
import { Switch } from 'react-router-dom';
import THNavBar from '@Organisms/THEntityLayout/THNavBar/THNavBar';
import PrivateRoute from '@Molecules/PrivateRoute/PrivateRoute';
import { HeaderActionType, RouteType } from '@Types';
import { THLayout } from '@Molecules/THLayout';
import { PinProvider } from '@Molecules/Pin';
import { TabProvider } from '@Context/TabProvider';
import { classList } from '@Helpers/className';
import UnknownRoute from '@Molecules/UnknownRoute/UnknownRoute';

import './THEntityLayout.scss';
import './THSubLayout.scss';
import './THLayoutSider.scss';

const { Content, Sider } = Layout;

interface THEntityLayoutProps {
    routes: RouteType[];
    title: ReactNode;
    actions: HeaderActionType[];
    leftSiderContent?: ReactNode;
    entityId?: string;
    paramName?: string;
    className?: string;
    extraContent?: ReactElement | ReactElement[];
    extraHeader?: ReactElement;
}

const THEntityLayout = ({
    routes,
    title,
    actions,
    leftSiderContent,
    entityId,
    className,
    paramName = 'caseId',
    extraContent,
    extraHeader,
}: THEntityLayoutProps): ReactElement | null => {
    return routes ? (
        <TabProvider>
            <THLayout.Provider>
                <Layout className={classList('th-entity', className)}>
                    <THHeader title={title} actions={actions} />
                    <Layout className="th-entity_content">
                        {leftSiderContent && (
                            <Sider className="th-content_sider" width="18rem">
                                {leftSiderContent}
                            </Sider>
                        )}
                        <Content className="th-content_content">
                            <PinProvider>
                                <Layout className="th-sub-layout">
                                    {extraHeader}
                                    <THNavBar
                                        entityId={entityId}
                                        routes={routes}
                                        paramName={paramName}
                                        extraContent={extraContent}
                                    />
                                    <Layout className="th-sub-layout_content">
                                        <Switch>
                                            {[
                                                ...routes.map(({ exact, path, content, Context, space, shared }) => (
                                                    <PrivateRoute
                                                        key={path}
                                                        path={path}
                                                        space={space}
                                                        shared={shared}
                                                        exact={exact}
                                                    >
                                                        {Context ? <Context>{content}</Context> : content}
                                                    </PrivateRoute>
                                                )),
                                                <UnknownRoute key="any-layout-route" />,
                                            ]}
                                        </Switch>
                                    </Layout>
                                </Layout>
                            </PinProvider>
                        </Content>
                    </Layout>
                </Layout>
            </THLayout.Provider>
        </TabProvider>
    ) : null;
};

export default THEntityLayout;
